// frontend/src/pages/AboutPage.tsx
import { Link } from 'react-router-dom';
import { InfoPageLayout } from './InfoPageLayout';

export function AboutPage() {
  return (
    <InfoPageLayout title="About Llambada" documentTitle="About Us" metaDescription="Llambada lets anyone build mini-apps by describing ideas to AI — no coding required. Learn how it works, explore examples, and start creating for free.">
      <h2>Build Software Without Writing Code</h2>
      <p>
        Llambada is a platform that makes software creation accessible to everyone. You don't 
        need to know how to code. You don't need a computer science degree. You just need an 
        idea and the ability to describe what you want.
      </p>
      <p>
        Simply tell our AI what your mini-app should do, and it will write all the code for you. 
        Test it, refine it through conversation, and share it with the world—all without touching 
        a single line of code.
      </p>

      <h2>Why We Built Llambada</h2>
      <p>
        We believe that the ability to create software shouldn't be limited to professional 
        developers. Millions of people have ideas for tools that could help them, their 
        communities, or their businesses—but they're locked out of the software creation 
        process because coding has always been a specialized skill.
      </p>
      <p>
        AI changes everything. Large language models can now write functional code based on 
        natural language descriptions. Llambada harnesses this capability and wraps it in a 
        platform that handles all the technical complexity—hosting, deployment, security—so 
        you can focus on what matters: your idea.
      </p>

      <h2>What Are Mini-Apps?</h2>
      <p>
        Mini-apps are small, focused applications that solve specific problems. Unlike massive 
        enterprise software, mini-apps do one thing well. Examples include:
      </p>
      <ul>
        <li>A tool that analyzes your resume and tailors it to job descriptions</li>
        <li>A calculator for specific industry formulas</li>
        <li>A converter between file formats</li>
        <li>A generator for custom documents or reports</li>
        <li>An analyzer for data you upload</li>
        <li>A simple game or interactive experience</li>
      </ul>
      <p>
        Because mini-apps are focused and specific, they're perfect candidates for AI generation. 
        The AI can understand your requirements and build something functional quickly, and you 
        can iterate on it through conversation until it's exactly what you need.
      </p>

      <h2>How It Works</h2>
      <ol>
        <li>
          <strong>Describe your idea:</strong> Tell the AI what you want your mini-app to do. 
          Be as specific or as vague as you like—the AI will ask clarifying questions if needed.
        </li>
        <li>
          <strong>AI builds it:</strong> The AI writes the frontend interface and backend logic. 
          You see your mini-app come to life in real-time.
        </li>
        <li>
          <strong>Test and refine:</strong> Try out your mini-app. If something isn't right, 
          just tell the AI what to change. "Make the button bigger," "Add a download option," 
          "Handle this edge case"—the AI understands and adjusts.
        </li>
        <li>
          <strong>Use or share:</strong> Keep your mini-app private for personal use, or publish 
          it for others to discover and use.
        </li>
      </ol>

      <h2>Earn Money as a Creator</h2>
      <p>
        When you publish a mini-app on Llambada, you become a creator. If your mini-app is 
        useful and people use it, you earn money. We share 70% of attributed revenue with 
        creators—one of the most generous revenue shares in the industry.
      </p>
      <p>
        This creates a virtuous cycle: creators are incentivized to build useful tools, users 
        get access to a growing library of mini-apps, and everyone benefits. The best mini-apps 
        rise to the top based on actual usage.
      </p>
      <p>
        <Link to="/creators">Learn more about becoming a creator →</Link>
      </p>

      <h2>Customization Through Forking</h2>
      <p>
        Found a mini-app that's almost perfect but not quite? You can create your own customized 
        version. The "Customize" feature lets you take any public mini-app and make it your own—
        adjusting the interface, changing the behavior, or adapting it to your specific needs.
      </p>
      <p>
        This means you're never starting from scratch. Browse the library of existing mini-apps, 
        find something close to what you need, and customize it. It's like having thousands of 
        starting templates created by the community.
      </p>

      <h2>Powered by Advanced AI</h2>
      <p>
        Llambada uses state-of-the-art AI models from leading providers to generate code. We 
        provide free AI credits to get you started, and power users can bring their own API keys 
        for unlimited usage.
      </p>
      <p>
        The AI doesn't just write code—it understands context, remembers your previous 
        instructions, and can explain what it's doing. Think of it as a knowledgeable 
        collaborator who happens to be fluent in every programming language.
      </p>

      <h2>Our Commitment</h2>
      <p>
        <strong>Accessibility:</strong> We believe software creation should be accessible to 
        everyone, regardless of technical background.
      </p>
      <p>
        <strong>Privacy:</strong> Your data is yours. We don't use your mini-apps or content 
        to train AI models. We're transparent about what we collect and why.
      </p>
      <p>
        <strong>Fair compensation:</strong> Creators deserve to be paid for their work. Our 
        70/30 revenue share reflects our belief that the people building useful tools should 
        receive the majority of the value they create.
      </p>
      <p>
        <strong>Continuous improvement:</strong> We're constantly improving the platform based 
        on user feedback. The AI gets smarter, the tools get better, and the possibilities expand.
      </p>

      <h2>Get Started</h2>
      <p>
        Ready to build your first mini-app? <Link to="/register">Create a free account</Link> and 
        start building in minutes. No credit card required, no technical knowledge needed—just 
        your imagination.
      </p>
    </InfoPageLayout>
  );
}